import { Dispatch } from "redux"

import DriveImages from "assets/driveImages"
import {
    AWS_BUCKET,
    STORAGE_AWS,
    STORAGE_GOOGLE,
    STORAGE_MICROSOFT,
    RECYCLE_INDEX,
} from "const"
import { ModalContent } from "client/parts/modal/styled"
import { ContentColumn } from "client/parts/layout"
import { MtDriveFile, MtDriveFolder, MtDriveVersion } from "server/models"
import {
    DriveOnSelectFile,
    DriveOnSelectFiles,
    DriveOnSelectVersion,

    setDriveAction,

    setDriveFolderActive,
    setDriveFolderSelected,
    setDriveFolderReloadList,

    setDriveFileActive,
    setDriveFileSelected,
    setDriveFileReloadList,
} from "client/store"

interface GetFileResponse {
    download: string
    preview: string
}
export const getFile = (file: MtDriveFile, version: MtDriveVersion) => {
    if (!file || !version) {
        const res: GetFileResponse = {
            download: null,
            preview: null,
        }
        return res
    }

    const { storage } = file
    const { storageId, storageSharedUrl } = version
    let download: string
    let preview: string
    let url: string
    switch (storage) {
        case STORAGE_AWS:
            url = `https://s3-eu-west-1.amazonaws.com/${AWS_BUCKET}/${storageId}`
            download = url
            preview = url
            break
        case STORAGE_GOOGLE:
            download = `https://drive.google.com/uc?export=view&id=${storageId}`
            preview = `https://drive.google.com/thumbnail?id=${storageId}&sz=w10000`
            break
        case STORAGE_MICROSOFT:
            download = storageSharedUrl
            preview = storageSharedUrl
            break
        default:
            download = null
            preview = null
    }
    const res: GetFileResponse = {
        download,
        preview,
    }
    return res
}

export const convertFileSize = (unformattedSize: number) => {
    let size = unformattedSize
    if (size < 1024) return `${size}B`
    size /= 1024; if (size < 1024) return `${size.toFixed(1)}KB`
    size /= 1024; if (size < 1024) return `${size.toFixed(1)}MB`
    size /= 1024; return `${size.toFixed(2)}GB`
}

export const parseName = (fullName: string) => {
    let name = fullName.trim()
    const counterStartIndex = name.lastIndexOf("(")
    const counterEndIndex = name.lastIndexOf(")")
    let counter = 0
    if (counterStartIndex > 0 && counterEndIndex > counterStartIndex && counterEndIndex === name.length - 1) {
        counter = parseInt(name.substring(counterStartIndex + 1, counterEndIndex), 10)
    }
    if (counter > 0) {
        name = name.substring(0, counterStartIndex).trim()
    }

    return { name, counter }
}

export const createFolderName = (folder: MtDriveFolder) => {
    if (folder.project) return `${folder.project.name} (${folder.project.publicId})`
    if (folder.product) return `${folder.product.name} (${folder.product.company.name})`
    return folder.copy ? `${folder.name} (${folder.copy})` : folder.name
}

export const createFileName = (file: MtDriveFile) => {
    let fileName = file.name
    if (file.copy > 0) fileName += ` (${file.copy})`
    return fileName
}

export const createRecycleFolderName = (folder: MtDriveFolder) => {
    const name = createFolderName(folder)
    return `${name}${RECYCLE_INDEX}${folder.id}`
}

export const createRecycleFileName = (file: MtDriveFile) => {
    const name = createFileName(file)
    return `${name}${RECYCLE_INDEX}${file.id}`
}

export const createDriveUrl = (drivePath: readonly MtDriveFolder[], baseRoute: string) => {
    const encodedPath: string[] = []
    if (drivePath) {
        // first item is root, don't add it to the url
        for (let i = 1; i < drivePath.length; i++) {
            const folder = drivePath[i]
            const pathName = folder.folderId === null
                ? createRecycleFolderName(folder)
                : createFolderName(folder)
            encodedPath.push(encodeURIComponent(pathName))
        }
    }
    return encodedPath.length > 0 ? `${baseRoute}/${encodedPath.join("/")}` : baseRoute
}

export const isRecycleOpened = (drivePath: readonly MtDriveFolder[]) => {
    let recycleOpened = false
    if (drivePath) {
        for (let i = 0; i < drivePath.length; i++) {
            const folder = drivePath[i]
            if (folder.id === null) {
                recycleOpened = true
                break
            }
        }
    }
    return recycleOpened
}

export const isProjectFolderOpened = (drivePath: readonly MtDriveFolder[]) => {
    let projectId = null
    if (drivePath) {
        for (let i = 0; i < drivePath.length; i++) {
            const folder = drivePath[i]
            if (folder.project) {
                projectId = folder.project.id
                break
            }
        }
    }
    return projectId
}

export const clearDriveSelection = (dispatch: Dispatch) => {
    dispatch(setDriveAction(null))

    dispatch(setDriveFolderSelected([]))
    dispatch(setDriveFolderActive(null))

    dispatch(setDriveFileSelected([]))
    dispatch(setDriveFileActive(null))
}

export const reloadDriveLists = (dispatch: Dispatch) => {
    dispatch(setDriveFolderReloadList(false))
    dispatch(setDriveFileReloadList(false))
    setTimeout(() => {
        dispatch(setDriveFolderReloadList(true))
        dispatch(setDriveFileReloadList(true))
    }, 0)
}

export const getFileLogo = (file: MtDriveFile, version: MtDriveVersion) => {
    let src: string
    if (version) {
        const { type } = version
        if (type.indexOf("image") >= 0) src = getFile(file, version).preview
        else if (type.indexOf("video") >= 0) src = DriveImages.video
        else if (type.indexOf("pdf") >= 0) src = DriveImages.pdf
        else if (type.indexOf("officedocument.word") >= 0) src = DriveImages.doc
        else if (type.indexOf("officedocument.spreadsheet") >= 0) src = DriveImages.xls
        else if (type.indexOf("text") >= 0) src = DriveImages.txt
        else if (type.indexOf("zip") >= 0) src = DriveImages.zip
        else if (type.indexOf("rar") >= 0) src = DriveImages.rar
        else if (type.indexOf("php") >= 0) src = DriveImages.php
        else if (type.indexOf("ppt") >= 0) src = DriveImages.ppt
        else src = DriveImages.unknown
    } else {
        src = DriveImages.unknown
    }
    return src
}

export const getDriveScrollableParent = (onSelect: DriveOnSelectFile | DriveOnSelectFiles | DriveOnSelectVersion) => {
    let scrollableParent: Element
    if (onSelect) scrollableParent = document.body.querySelector(ModalContent)
    else scrollableParent = document.body.querySelector(ContentColumn)
    return scrollableParent
}
